import {
	useCreateProjectMutation,
	useCreateProjectAdditionalContactInformationMutation,
} from "@repo/server-api";
import {
	ResultFromData,
	ResultFromError,
	ResultFromException,
	useNotificationGraphql,
} from "@repo/shared/common";
import { useTranslation } from "react-i18next";

import type { NewProject } from "../types/ProjectTypes";
import type {
	GraphqlQueryResult} from "@repo/shared/common";

type UseCreateNewProjectOptions = {
	onSaved: (id: string) => void;
};

type UseCreateNewProjectResult = {
	save: (project: NewProject) => void;
	loading: boolean;
}

// Here is the hook
export function useCreateNewProject(
	options: UseCreateNewProjectOptions,
): UseCreateNewProjectResult {
	const { t } = useTranslation();

	const [createProjectMutation] = useCreateProjectMutation();
	const [createAdditionalContactInfoMutation] =
		useCreateProjectAdditionalContactInformationMutation();

	const createProject = async (project: NewProject): Promise<GraphqlQueryResult<string>> => {
		const createdProject = await createProjectMutation({
			variables: {
				name: project.name,
				type: project.type,
				from: project.startDate!,
				to: project.endDate!,
				parentProject: project.parentProject?.id,
			},
		});
		if (
			createdProject.data?.createProject.errors &&
			createdProject.data?.createProject.errors.length > 0
		) {
			return ResultFromError<string>(createdProject.data.createProject.errors);
		}
		if (createdProject.errors) return ResultFromException<string>(createdProject.errors);

		const projectId = createdProject.data?.createProject.project?.id;
		if (!projectId) return ResultFromException<string>(t("no-project-id-returned"));

		// Create additional contact information
		await createAdditionalContactInfoMutation({
			variables: {
				projectId,
				clientId: project.client?.id,
				contractorId: project.contractor.id,
			},
		});

		return ResultFromData<string>(projectId);
	};

	const { save, loading } = useNotificationGraphql<NewProject, string>({
		onComplete: (id) => {
			options.onSaved(id);
		},

		graphqlCall: createProject,

		successMessage: t("project-created-successfully"),
	});

	return {
		save,
		loading,
	};
}
